import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { RiCloseLine, RiSendPlane2Line } from '@remixicon/react';
import { toast } from 'sonner';
import PhoneInput from '@components/forms/PhoneInput';
import { COUNTRY_CODES } from '@/utils/countryCode';
import { maskPhone } from '../utils/format';
import VerifyCodeStep from './VerifyCodeStep';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@store/authStore';
import { useMfaStepUpGate } from '../hooks/MfaStepUpGate';

// Flip to true to require SMS verification before saving.
const VERIFY_PHONE_ENABLED = true;

// Split a stored E.164-ish phone ("+19999999999") into { code, digits }.
const splitPhone = (raw, fallbackCode = '+1') => {
  const value = (raw || '').trim();
  if (!value) return { code: fallbackCode, digits: '' };
  if (value.startsWith('+')) {
    const sorted = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);
    const match = sorted.find((c) => value.startsWith(c.code));
    if (match) return { code: match.code, digits: value.slice(match.code.length).replace(/\D/g, '') };
  }
  return { code: fallbackCode, digits: value.replace(/\D/g, '') };
};

const UpdatePhoneModal = ({ open, onClose, onSubmit, initialCountryCode = '+1', initialPhone = '' }) => {
  const initial = splitPhone(initialPhone, initialCountryCode);
  const { userProfile } = useAuthStore();
  const { gate } = useMfaStepUpGate();
  const [step, setStep] = useState('enter');
  const [countryCode, setCountryCode] = useState(initial.code);
  const [phone, setPhone] = useState(initial.digits);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const verificationCodeRef = useRef(null);

  useEffect(() => {
    if (open) {
      const next = splitPhone(initialPhone, initialCountryCode);
      setStep('enter');
      setCountryCode(next.code);
      setPhone(next.digits);
      setSaving(false);
      setSending(false);
    }
  }, [open, initialCountryCode, initialPhone]);

  if (!open) return null;

  const hasChanges = countryCode !== initial.code || phone !== initial.digits;
  const canSubmit = phone.replace(/\D/g, '').length >= 7 && !saving && !sending && hasChanges;

  const sendVerificationCode = async () => {
    await gate();
    const code = String(Math.floor(100000 + Math.random() * 900000));
    verificationCodeRef.current = code;
    const toPhone = `${countryCode}${phone.replace(/\D/g, '')}`;
    setSending(true);
    try {

      await apiClient.sendSms({
        to_phone: toPhone,
        message_body: `Your TrustJet verification code is ${code}. Do not share this code with anyone. If you did not request this code, please ignore this message.`,
      });

      if (userProfile?.userprofile_id) {
        await apiClient.updateUser(userProfile.userprofile_id, {
          preferences: {
            ...(userProfile.preferences || {}),
            phone_verification_code: code,
          },
        });
      }
      toast.success('Verification code sent to your phone', { position: 'bottom-right' });
    } catch (err) {
      toast.error(err?.message || 'Failed to send verification code', { position: 'bottom-right' });
      throw err;
    } finally {
      setSending(false);
    }
  };

  const savePhone = async () => {
    setSaving(true);
    try {
      await onSubmit?.({ countryCode, phone });
      onClose();
      toast.success('Phone updated successfully', { position: 'bottom-right' });
    } catch (err) {
      toast.error(err?.message || 'Failed to update phone', { position: 'bottom-right' });
    } finally {
      setSaving(false);
    }
  };

  const handlePrimaryAction = async () => {
    if (!canSubmit) return;
    if (VERIFY_PHONE_ENABLED) {
      try {
        await sendVerificationCode();
        setStep('verify');
      } catch {
        // toast already shown
      }
    } else {
      savePhone();
    }
  };

  const handleResend = async () => {
    try {
      await sendVerificationCode();
    } catch {
      // toast already shown
    }
  };

  const handleVerified = async (code) => {
    try {
      const profile = await apiClient.getUserProfile();
      const storedCode = profile?.preferences?.phone_verification_code;
      if (!storedCode || code !== String(storedCode)) {
        toast.error('Invalid verification code', { position: 'bottom-right' });
        throw new Error('Invalid verification code');
      }
    } catch (err) {
      if (err?.message === 'Invalid verification code') throw err;
      toast.error('Could not verify code. Please try again.', { position: 'bottom-right' });
      throw err;
    }
    await savePhone();
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/30" onClick={onClose} />
      <div className="fixed z-[9999] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-md bg-white rounded-3xl shadow-xl overflow-hidden">
        {step === 'enter' ? (
          <>
            <div className="flex items-center justify-between px-6 pt-5 pb-1">
              <h3 className="text-lg font-semibold text-primary-700">Update phone number</h3>
              <button
                type="button"
                onClick={onClose}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <RiCloseLine className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 pt-3 pb-6">
              <PhoneInput
                countryCode={countryCode}
                phone={phone}
                onCountryCodeChange={setCountryCode}
                onPhoneChange={setPhone}
                label="New Phone Number"
                required
              />
              <button
                type="button"
                onClick={handlePrimaryAction}
                disabled={!canSubmit}
                className={`mt-5 w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-medium transition-colors ${
                  canSubmit
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                <RiSendPlane2Line className="w-4 h-4" />
                {sending ? 'Sending...' : saving ? 'Saving...' : 'Send verification code'}
              </button>
            </div>
          </>
        ) : (
          <VerifyCodeStep
            maskedPhone={maskPhone(phone.replace(/\D/g, ''))}
            onResend={handleResend}
            onVerified={handleVerified}
            onClose={onClose}
          />
        )}
      </div>
    </>,
    document.body
  );
};

export default UpdatePhoneModal;
